import type { FrameSpec } from '../config/frames';

// Input bounds protect the calibration; they are not a population reference.
export const PD_MIN_MM = 40;
export const PD_MAX_MM = 85;
export function validatePd(mm: number | null): void {
  if (mm !== null && (!Number.isFinite(mm) || mm < PD_MIN_MM || mm > PD_MAX_MM)) {
    throw new RangeError('PD must be a total binocular distance between 40 and 85 mm, or null');
  }
}

/** Target total front widths agreed for the Handmade preview. M is the default. */
export const HANDMADE_SIZES = [
  { label: 'XS', widthMm: 135 }, { label: 'S', widthMm: 140 },
  { label: 'M', widthMm: 145 }, { label: 'L', widthMm: 150 }, { label: 'XL', widthMm: 155 },
] as const;
export function handmadeScaleForWidth(spec: FrameSpec, widthMm: number): number {
  if (spec.line !== 'Handmade') throw new Error('Fixed-size products cannot be resized');
  if (!HANDMADE_SIZES.some(size => size.widthMm === widthMm)) throw new RangeError('Unknown Handmade width');
  if (!(spec.dims.widthMm > 0) || !Number.isFinite(spec.dims.widthMm)) throw new RangeError('Invalid reference width');
  const scale = widthMm / spec.dims.widthMm;
  if (scale < HANDMADE_MIN_SCALE || scale > HANDMADE_MAX_SCALE) throw new RangeError('Normalize the source frame near 145 mm before enabling standard sizes');
  return scale;
}

export const HANDMADE_MIN_SCALE = 0.8;
export const HANDMADE_MAX_SCALE = 1.2;

/** Effective render dimensions. Does not rebuild geometry or change face fit. */
export function sizeHandmade(spec: FrameSpec, scale: number): FrameSpec {
  if (!Number.isFinite(scale) || scale < HANDMADE_MIN_SCALE || scale > HANDMADE_MAX_SCALE) throw new RangeError('Invalid handmade scale');
  if (scale === 1) return spec;
  if (spec.line !== 'Handmade') throw new Error('Fixed-size products cannot be resized');
  const dims = { ...spec.dims };
  for (const key of Object.keys(dims) as Array<keyof typeof dims>) {
    const value = dims[key];
    if (value !== undefined) dims[key] = value * scale;
  }
  const contour = (points: Array<[number, number]>): Array<[number, number]> => points.map(([x, y]) => [x * scale, y * scale]);
  const s = spec.silhouette;
  const temple = spec.temple;
  return {
    ...spec, dims,
    look: { ...spec.look, frontPhotoLensInsetMm: spec.look.frontPhotoLensInsetMm === undefined ? undefined : spec.look.frontPhotoLensInsetMm * scale, butterflyRivets: spec.look.butterflyRivets?.map(([x,y]) => [x*scale,y*scale]), frontPhotoUniformBelowMm: spec.look.frontPhotoUniformBelowMm?.map(y => y * scale) as [number, number] | undefined },
    hybridMetal: spec.hybridMetal && {
      ...spec.hybridMetal,
      front: spec.hybridMetal.front.map(r => ({ ...r, fromMm:r.fromMm*scale, toMm:r.toMm*scale, depthMm:r.depthMm*scale, yMinMm:r.yMinMm === undefined ? undefined : r.yMinMm*scale, yMaxMm:r.yMaxMm === undefined ? undefined : r.yMaxMm*scale, transitionMm:r.transitionMm === undefined ? undefined : r.transitionMm*scale })),
      temple: spec.hybridMetal.temple.map(r => ({ ...r, fromMm:r.fromMm*scale, toMm:r.toMm*scale, depthMm:r.depthMm*scale, yMinMm:r.yMinMm === undefined ? undefined : r.yMinMm*scale, yMaxMm:r.yMaxMm === undefined ? undefined : r.yMaxMm*scale, transitionMm:r.transitionMm === undefined ? undefined : r.transitionMm*scale })),
      nosePads: spec.hybridMetal.nosePads && {
        ...spec.hybridMetal.nosePads,
        center: spec.hybridMetal.nosePads.center.map(v=>v*scale) as [number,number,number],
        size: spec.hybridMetal.nosePads.size.map(v=>v*scale) as [number,number,number],
        arm: spec.hybridMetal.nosePads.arm.map(p=>p.map(v=>v*scale) as [number,number,number]),
        armRadiusMm: spec.hybridMetal.nosePads.armRadiusMm*scale,
      },
    },
    rearLaminate: spec.rearLaminate && { ...spec.rearLaminate, outer: contour(spec.rearLaminate.outer), thicknessMm: spec.rearLaminate.thicknessMm * scale },
    silhouette: s ? {
      ...s, outer: contour(s.outer), lenses: [contour(s.lenses[0]), contour(s.lenses[1])],
      templeLensOpenings: s.templeLensOpenings?.map(contour),
      decorativeCutouts: s.decorativeCutouts?.map(contour),
      raisedRims: s.raisedRims?.map(rim => ({ ...rim, outer: contour(rim.outer), depthMm: rim.depthMm * scale })),
      rearOcclusionRegions: s.rearOcclusionRegions?.map(contour),
      stitchPaths: s.stitchPaths?.map(contour),
      stitchRadiusMm: s.stitchRadiusMm === undefined ? undefined : s.stitchRadiusMm * scale,
      stitchPhoto: s.stitchPhoto && { ...s.stitchPhoto, pxPerMm: s.stitchPhoto.pxPerMm / scale },
      hingeY: s.hingeY * scale, lensHeightMm: s.lensHeightMm * scale,
      lensFrontInsetMm: s.lensFrontInsetMm === undefined ? undefined : s.lensFrontInsetMm * scale,
      templeTop: s.templeTop && contour(s.templeTop), templeBottom: s.templeBottom && contour(s.templeBottom),
      frontPhoto: s.frontPhoto && { ...s.frontPhoto, pxPerMm: s.frontPhoto.pxPerMm / scale },
      templePhoto: s.templePhoto && { ...s.templePhoto, pxPerMm: s.templePhoto.pxPerMm / scale },
    } : undefined,
    temple: temple ? {
      ...temple, profileHeightMm: temple.profileHeightMm === undefined ? undefined : temple.profileHeightMm * scale, junctionHeightMm: temple.junctionHeightMm === undefined ? undefined : temple.junctionHeightMm * scale, topAtHingeMm: temple.topAtHingeMm === undefined ? undefined : temple.topAtHingeMm * scale, widthMm: temple.widthMm * scale, topInsetMm: temple.topInsetMm * scale,
      hingeInsetMm: temple.hingeInsetMm * scale, outerInsetMm: temple.outerInsetMm * scale,
      profileStartMm: temple.profileStartMm * scale,
      hingeOffsetYMm: temple.hingeOffsetYMm?.map(y => y * scale) as [number, number] | undefined,
      profileFitRangeMm: temple.profileFitRangeMm && [temple.profileFitRangeMm[0] * scale, temple.profileFitRangeMm[1] * scale],
    } : undefined,
  };
}
